import { useMemo, useRef, useState } from 'react'
import { fbt, useLocaleContext } from 'fbtee'
import { Download, Loader2 } from 'lucide-react'
import { APP_NAME } from '@shared/brand'
import { APP_ORIGIN } from '@shared/origin'
import { parseDateOnly, toBcp47 } from '@shared/dates'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useDayCutoff } from '@/contexts/DayCutoffContext'
import { useTimeFormat } from '@/hooks/useTimeFormat'
import { useScrollEdges } from '@/hooks/useScrollEdges'
import { edgeMask } from '@/lib/edgeMask'
import { track, type ScheduleSurface } from '@/lib/analytics'
import { saveImageFile } from '@/lib/imageExport'
import { layoutWallpaper, wallpaperAspect, wallpaperDayRange, wallpaperPickStatus, type WallpaperOptions as Options } from '@/lib/scheduleWallpaper'
import { classifyError } from '@/lib/errors'
import { toastError } from '@/lib/toast'
import { log } from '@/lib/logger'
import type { Artist, PartialRange, Set as FestivalSet, Stage } from '@/types'
import { minutesToClockLabel, minutesToHourLabel } from '../timeUtils'
import { exportWallpaper, readWallpaperTheme, type WallpaperDrawing, type WallpaperText } from './drawWallpaper'
import { WallpaperOptions } from './WallpaperOptions'
import { WallpaperPreview } from './WallpaperPreview'
import { useWallpaperAssets } from './useWallpaperAssets'

const APP_HOST = new URL(APP_ORIGIN).host

interface WallpaperDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  eventId: string
  eventName: string
  surface: ScheduleSurface
  days: string[]
  initialDay: string
  sets: FestivalSet[]
  /** Every stage in the user's order; `hidden` seeds the stage toggles. */
  stages: Array<Stage & { hidden: boolean }>
  artists: Record<string, Artist>
  interestedSetIds: ReadonlySet<string>
  goingSetIds: ReadonlySet<string>
  /** The viewer's own partial set ranges, drawn like the schedule draws them. */
  ownRanges?: ReadonlyMap<string, PartialRange>
}

const NO_RANGES: ReadonlyMap<string, PartialRange> = new Map()

// "Lock screen image" (#658): one day of the schedule as a phone-sized image,
// with a live preview of exactly what gets saved.
export function WallpaperDialog({ open, onOpenChange, ...props }: WallpaperDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92dvh] grid-rows-[auto_auto_minmax(0,1fr)_auto] gap-3" onOpenAutoFocus={e => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>
            <fbt desc="Title of the dialog that makes a lock screen image of a schedule day">Lock screen image</fbt>
          </DialogTitle>
          <DialogDescription>
            <fbt desc="Description of the lock screen image dialog">
              One day of your schedule, sized for your phone's lock screen.
            </fbt>
          </DialogDescription>
        </DialogHeader>
        {/* Content unmounts while closed, so every open starts from fresh settings. */}
        <WallpaperDialogBody {...props} />
      </DialogContent>
    </Dialog>
  )
}

function WallpaperDialogBody({
  eventId, eventName, surface, days, initialDay, sets, stages, artists, interestedSetIds, goingSetIds, ownRanges = NO_RANGES,
}: Omit<WallpaperDialogProps, 'open' | 'onOpenChange'>) {
  const cutoff = useDayCutoff()
  const [options, setOptions] = useState<Options>(() => ({
    day: initialDay,
    highlight: sets.some(s => s.day === initialDay && wallpaperPickStatus(s.id, interestedSetIds, goingSetIds))
      ? 'interested'
      : 'none',
    hiddenStageIds: new Set(stages.filter(s => s.hidden).map(s => s.id)),
  }))

  const [aspect] = useState(() => wallpaperAspect(window.screen))
  const [theme] = useState(readWallpaperTheme)
  const layout = useMemo(
    () => layoutWallpaper({ sets, stages, interestedSetIds, goingSetIds, options, cutoff, aspect }),
    [sets, stages, interestedSetIds, goingSetIds, options, cutoff, aspect],
  )
  const dayStages = useMemo(() => {
    const ids = new Set(sets.filter(s => s.day === options.day && s.stageId).map(s => s.stageId))
    return stages.filter(s => ids.has(s.id))
  }, [sets, stages, options.day])

  const dayRange = useMemo(() => wallpaperDayRange(sets, options.day, cutoff), [sets, options.day, cutoff])
  const { hourCycle, locale } = useTimeFormat()
  const text = useWallpaperText(eventName, options.day)
  const { images, logo, fontsReady, whenReady } = useWallpaperAssets(layout, artists)
  const drawing: WallpaperDrawing = useMemo(
    () => ({ layout, artists, text, theme, images, logo, ranges: ownRanges }),
    // fontsReady: a canvas draws with a fallback font until the web font loads, so redraw once it has.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [layout, artists, text, theme, images, logo, ownRanges, fontsReady],
  )
  const [saving, setSaving] = useState(false)
  // The options scroll under the preview on short screens; fade the clipped edge so it's clear there's more.
  const optionsRef = useRef<HTMLDivElement>(null)
  const { overflowLeft: moreAbove, overflowRight: moreBelow } = useScrollEdges(optionsRef, 'y')
  const optionsMask = edgeMask(moreAbove, moreBelow, '28px', 'y')

  async function save() {
    setSaving(true)
    try {
      const startedAt = performance.now()
      const assets = await whenReady()
      const image = await exportWallpaper({ ...drawing, ...assets })
      log('wallpaper', 'rendered', { ms: Math.round(performance.now() - startedAt), bytes: image.size })
      const method = await saveImageFile(image, `${eventId}-${options.day}.png`, {
        title: fbt(
          'My ' + fbt.param('event', eventName) + ' schedule',
          'Share sheet message when sharing your festival schedule',
        ) as unknown as string,
      })
      if (method !== 'cancelled') track('schedule_wallpaper_saved', { event_id: eventId, surface, method })
    } catch (err) {
      toastError(fbt("Couldn't create the image", 'Error toast when making a lock screen image fails'), classifyError(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <WallpaperPreview drawing={drawing} />
      <div
        ref={optionsRef}
        className="-mx-4 overflow-y-auto px-4"
        style={{ maskImage: optionsMask, WebkitMaskImage: optionsMask }}
      >
        <WallpaperOptions
          options={options}
          onChange={setOptions}
          days={days}
          dayStages={dayStages}
          dayRange={dayRange}
          hourLabel={m => minutesToClockLabel(m, hourCycle, locale)}
        />
      </div>
      <Button onClick={save} disabled={!!layout.empty || saving} className="w-full">
        {saving ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
        <fbt desc="Button that saves or shares the lock screen image of a schedule day">Save image</fbt>
      </Button>
    </>
  )
}

function useWallpaperText(eventName: string, day: string): WallpaperText {
  const { locale } = useLocaleContext()
  const { hourCycle, formatClock, formatClockRange } = useTimeFormat()
  return useMemo(() => ({
    eventName,
    dateLabel: capitalize(
      parseDateOnly(day).toLocaleDateString(toBcp47(locale), { weekday: 'long', month: 'long', day: 'numeric' }),
    ),
    appName: APP_NAME,
    footer: String(fbt(
      'Make yours at ' + fbt.param('site', APP_HOST),
      'Lock screen image footer inviting whoever sees it to make their own; site is the app domain, e.g. faro.dance',
    )),
    emptyLabel: {
      no_stages: String(fbt('No stages shown', 'Lock screen image placeholder when every stage is hidden')),
      no_sets: String(fbt('No sets in these hours', 'Lock screen image placeholder when no set plays in the picked hours')),
    },
    hourLabel: (minutes: number) => minutesToHourLabel(minutes, hourCycle),
    clock: formatClock,
    clockRange: formatClockRange,
  }), [eventName, day, locale, hourCycle, formatClock, formatClockRange])
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}
